import { AlertTriangle } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export interface AlfredDeviceDeleteIdentity {
  deviceId: string
  clientId: string
}

interface DeleteAlfredDeviceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  devices: ReadonlyArray<AlfredDeviceDeleteIdentity>
  onConfirm: () => void
  isLoading?: boolean
}

export function DeleteAlfredDeviceDialog({
  open,
  onOpenChange,
  devices = [],
  onConfirm,
  isLoading = false,
}: DeleteAlfredDeviceDialogProps) {
  const isBulk = devices.length > 1
  const displayId = devices[0]?.deviceId ?? ""

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[calc(100vw-2rem)] border-border bg-card sm:max-w-xl">
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/20">
              <AlertTriangle className="size-5 text-destructive" />
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-foreground">
                {isBulk ? `Delete ${devices.length} devices?` : "Delete device?"}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground mt-2">
                {isBulk ? (
                  <>
                    This will permanently delete{" "}
                    <span className="font-semibold text-foreground">
                      {devices.length} devices
                    </span>. This action cannot be undone.
                  </>
                ) : (
                  <>
                    This will permanently delete{" "}
                    <span className="font-mono text-foreground">{displayId}</span>. This action cannot be undone.
                  </>
                )}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <ScrollArea
          className="h-60 rounded-md border border-border"
          role="region"
          aria-label="Devices to delete"
        >
          <Table className="table-fixed">
            <TableHeader className="sticky top-0 bg-card">
              <TableRow>
                <TableHead className="w-1/2">Device ID</TableHead>
                <TableHead className="w-1/2">Client ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={`${device.clientId}:${device.deviceId}`}>
                  <TableCell className="whitespace-normal break-all font-mono">
                    {device.deviceId}
                  </TableCell>
                  <TableCell className="whitespace-normal break-all font-mono">
                    {device.clientId}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel
            disabled={isLoading}
            className="bg-secondary border-input text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
            }}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {isLoading ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
